/**
 * AAPOR outcome rates from final disposition counts.
 *
 * The formulas are those of *Standard Definitions: Final Dispositions of Case Codes and
 * Outcome Rates for Surveys* (AAPOR, 10th edition, 2023), Section 7, in which:
 *
 * - I = complete interview (1.1), P = partial interview (1.2)
 * - R = refusal and break-off (2.10), NC = non-contact (2.20), O = other eligible
 *   non-interview (2.30, 2.90)
 * - UH = unknown if household/occupied housing unit (3.10), UR = unknown if the sampled
 *   unit is eligible (3.20), UO = unknown, other (3.90)
 * - e = the estimated proportion of cases of unknown eligibility that are eligible
 *
 * Ineligible cases (category 4) are in no denominator. The module computes unweighted
 * rates from counts; the standard's weighted versions replace each count with a weighted
 * total and take the same form.
 */

export interface Dispositions {
  I: number;
  P: number;
  R: number;
  NC: number;
  O: number;
  UH: number;
  UR: number;
  UO: number;
}

export type RateId =
  | "RR1"
  | "RR2"
  | "RR3"
  | "RR4"
  | "RR5"
  | "RR6"
  | "COOP1"
  | "COOP2"
  | "COOP3"
  | "COOP4"
  | "REF1"
  | "REF2"
  | "REF3"
  | "CON1"
  | "CON2"
  | "CON3";

export type RateGroup = "response" | "cooperation" | "refusal" | "contact";

export interface Rate {
  id: RateId;
  group: RateGroup;
  /** The formula as AAPOR prints it. */
  formula: string;
  numerator: number;
  denominator: number;
  /** A share from 0 to 1, or null when the denominator is zero or e is needed and missing. */
  value: number | null;
  /** True for the rates that carry e. */
  usesE: boolean;
}

export const DISPOSITION_KEYS = [
  "I",
  "P",
  "R",
  "NC",
  "O",
  "UH",
  "UR",
  "UO",
] as const;

export type DispositionKey = (typeof DISPOSITION_KEYS)[number];

const F_RR_DEN = "(I + P) + (R + NC + O) + (UH + UR + UO)";
const F_RR_DEN_E = "(I + P) + (R + NC + O) + e(UH + UR + UO)";
const F_RR_DEN_KNOWN = "(I + P) + (R + NC + O)";
const F_CON_NUM = "(I + P) + R + O";

interface Spec {
  id: RateId;
  group: RateGroup;
  numerator: (d: Dispositions) => number;
  /** The denominator, given the sums and e. */
  denominator: (d: Dispositions, e: number) => number;
  numeratorFormula: string;
  denominatorFormula: string;
  usesE: boolean;
}

const interviews = (d: Dispositions) => d.I + d.P;
const nonInterviews = (d: Dispositions) => d.R + d.NC + d.O;
const unknown = (d: Dispositions) => d.UH + d.UR + d.UO;
const contacted = (d: Dispositions) => d.I + d.P + d.R + d.O;

const SPECS: readonly Spec[] = [
  {
    id: "RR1",
    group: "response",
    numerator: (d) => d.I,
    denominator: (d) => interviews(d) + nonInterviews(d) + unknown(d),
    numeratorFormula: "I",
    denominatorFormula: F_RR_DEN,
    usesE: false,
  },
  {
    id: "RR2",
    group: "response",
    numerator: interviews,
    denominator: (d) => interviews(d) + nonInterviews(d) + unknown(d),
    numeratorFormula: "I + P",
    denominatorFormula: F_RR_DEN,
    usesE: false,
  },
  {
    id: "RR3",
    group: "response",
    numerator: (d) => d.I,
    denominator: (d, e) => interviews(d) + nonInterviews(d) + e * unknown(d),
    numeratorFormula: "I",
    denominatorFormula: F_RR_DEN_E,
    usesE: true,
  },
  {
    id: "RR4",
    group: "response",
    numerator: interviews,
    denominator: (d, e) => interviews(d) + nonInterviews(d) + e * unknown(d),
    numeratorFormula: "I + P",
    denominatorFormula: F_RR_DEN_E,
    usesE: true,
  },
  {
    id: "RR5",
    group: "response",
    numerator: (d) => d.I,
    denominator: (d) => interviews(d) + nonInterviews(d),
    numeratorFormula: "I",
    denominatorFormula: F_RR_DEN_KNOWN,
    usesE: false,
  },
  {
    id: "RR6",
    group: "response",
    numerator: interviews,
    denominator: (d) => interviews(d) + nonInterviews(d),
    numeratorFormula: "I + P",
    denominatorFormula: F_RR_DEN_KNOWN,
    usesE: false,
  },
  {
    id: "COOP1",
    group: "cooperation",
    numerator: (d) => d.I,
    denominator: (d) => interviews(d) + d.R + d.O,
    numeratorFormula: "I",
    denominatorFormula: "(I + P) + R + O",
    usesE: false,
  },
  {
    id: "COOP2",
    group: "cooperation",
    numerator: interviews,
    denominator: (d) => interviews(d) + d.R + d.O,
    numeratorFormula: "I + P",
    denominatorFormula: "(I + P) + R + O",
    usesE: false,
  },
  {
    id: "COOP3",
    group: "cooperation",
    numerator: (d) => d.I,
    denominator: (d) => interviews(d) + d.R,
    numeratorFormula: "I",
    denominatorFormula: "(I + P) + R",
    usesE: false,
  },
  {
    id: "COOP4",
    group: "cooperation",
    numerator: interviews,
    denominator: (d) => interviews(d) + d.R,
    numeratorFormula: "I + P",
    denominatorFormula: "(I + P) + R",
    usesE: false,
  },
  {
    id: "REF1",
    group: "refusal",
    numerator: (d) => d.R,
    denominator: (d) => interviews(d) + nonInterviews(d) + unknown(d),
    numeratorFormula: "R",
    denominatorFormula: F_RR_DEN,
    usesE: false,
  },
  {
    id: "REF2",
    group: "refusal",
    numerator: (d) => d.R,
    denominator: (d, e) => interviews(d) + nonInterviews(d) + e * unknown(d),
    numeratorFormula: "R",
    denominatorFormula: F_RR_DEN_E,
    usesE: true,
  },
  {
    id: "REF3",
    group: "refusal",
    numerator: (d) => d.R,
    denominator: (d) => interviews(d) + nonInterviews(d),
    numeratorFormula: "R",
    denominatorFormula: F_RR_DEN_KNOWN,
    usesE: false,
  },
  {
    id: "CON1",
    group: "contact",
    numerator: contacted,
    denominator: (d) => interviews(d) + nonInterviews(d) + unknown(d),
    numeratorFormula: F_CON_NUM,
    denominatorFormula: F_RR_DEN,
    usesE: false,
  },
  {
    id: "CON2",
    group: "contact",
    numerator: contacted,
    denominator: (d, e) => interviews(d) + nonInterviews(d) + e * unknown(d),
    numeratorFormula: F_CON_NUM,
    denominatorFormula: F_RR_DEN_E,
    usesE: true,
  },
  {
    id: "CON3",
    group: "contact",
    numerator: contacted,
    denominator: (d) => interviews(d) + nonInterviews(d),
    numeratorFormula: F_CON_NUM,
    denominatorFormula: F_RR_DEN_KNOWN,
    usesE: false,
  },
];

/** "I + P" → "(I + P)"; a lone letter stays as it is. */
function wrap(formula: string): string {
  return formula.includes(" + ") ? `(${formula})` : formula;
}

/**
 * Every AAPOR rate for a set of final dispositions. `e` is the estimated eligible share
 * of the unknown cases (0–1); with `null`, the four rates that need it have no value.
 */
export function outcomeRates(d: Dispositions, e: number | null): Rate[] {
  return SPECS.map((spec) => {
    const needsMissing = spec.usesE && e === null;
    const numerator = spec.numerator(d);
    const denominator = spec.denominator(d, e ?? 0);
    const value =
      needsMissing || denominator <= 0 ? null : numerator / denominator;
    return {
      id: spec.id,
      group: spec.group,
      formula: `${wrap(spec.numeratorFormula)} / (${spec.denominatorFormula})`,
      numerator,
      denominator: needsMissing ? Number.NaN : denominator,
      value,
      usesE: spec.usesE,
    };
  });
}

/**
 * Proportional allocation: assume the cases of unknown eligibility are eligible at the same
 * rate as the cases whose status is known — e = eligible / (eligible + ineligible). One of
 * the standard's recognised ways to estimate e; null when no cases are known.
 */
export function proportionalE(
  d: Dispositions,
  ineligible: number
): number | null {
  const eligible = d.I + d.P + d.R + d.NC + d.O;
  const known = eligible + ineligible;
  return known > 0 ? eligible / known : null;
}

export function totalCases(d: Dispositions, ineligible: number): number {
  return (
    d.I + d.P + d.R + d.NC + d.O + d.UH + d.UR + d.UO + Math.max(0, ineligible)
  );
}
